import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Search, History } from 'lucide-react';
import { tvKey } from './tvKeys';
import { popNav, popOk, popKey, isKidProfile, isKidContent } from './tvAssets';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.split('');
const KB_COLS = 6;
const RES_COLS = 3;
const SPECIALS = ['ESPACE', 'SUPPR', 'EFFACER'];
const HIST_KEY = 'tvSearchHistory';

function loadHist() {
  try { return JSON.parse(localStorage.getItem(HIST_KEY) || '[]'); } catch { return []; }
}

export function TvSearch({ onClose, onOpenDetail, active }) {
  const { activeProfile } = useAuth();
  const kid = isKidProfile(activeProfile);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [zone, setZone] = useState('kb');
  const [kbIdx, setKbIdx] = useState(0);
  const [resIdx, setResIdx] = useState(0);
  const [hist, setHist] = useState(loadHist);
  const [histIdx, setHistIdx] = useState(0);
  const debounceRef = useRef(null);

  const saveHist = useCallback((q) => {
    if (!q) return;
    setHist(prev => {
      const next = [q, ...prev.filter(x => x !== q)].slice(0, 8);
      try { localStorage.setItem(HIST_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); setSearched(false); setZone('kb'); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/search`, { params: { q: query.trim() }, withCredentials: true });
        setResults((data || []).filter(c => kid ? isKidContent(c) : c.type !== 'game'));
        setSearched(true);
        setResIdx(0);
      } catch { /* ignore */ }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const pressKey = useCallback((idx) => {
    if (idx < 36) setQuery(q => (q + LETTERS[idx]).slice(0, 40));
    else if (idx === 36) setQuery(q => (q + ' ').slice(0, 40));
    else if (idx === 37) setQuery(q => q.slice(0, -1));
    else if (idx === 38) setQuery('');
  }, []);

  useEffect(() => {
    if (!active) return;
    const showHistory = !query.trim() && hist.length > 0;
    const onKey = (e) => {
      const k = tvKey(e);
      if (!k) return;
      e.preventDefault();
      if (k === 'back') { onClose(); return; }
      if (zone === 'back') {
        if (k === 'down') { popNav(); setZone('kb'); }
        else if (k === 'ok') { popOk(); onClose(); }
        return;
      }
      if (k === 'ok') { if (zone === 'kb') popKey(); else popOk(); }
      if (zone === 'kb') {
        if (k === 'ok') { pressKey(kbIdx); return; }
        if (kbIdx < 36) {
          const r = Math.floor(kbIdx / KB_COLS), c = kbIdx % KB_COLS;
          if (k === 'left') { if (c > 0) popNav(); setKbIdx(r * KB_COLS + Math.max(0, c - 1)); }
          else if (k === 'right') {
            if (c === KB_COLS - 1 && results.length) { popNav(); setZone('results'); }
            else if (c === KB_COLS - 1 && showHistory) { popNav(); setZone('history'); setHistIdx(0); }
            else { if (c < KB_COLS - 1) popNav(); setKbIdx(r * KB_COLS + Math.min(KB_COLS - 1, c + 1)); }
          } else if (k === 'up') {
            if (r === 0) { popNav(); setZone('back'); }
            else { popNav(); setKbIdx((r - 1) * KB_COLS + c); }
          }
          else if (k === 'down') {
            popNav();
            if (r === 5) setKbIdx(36 + Math.min(2, Math.floor(c / 2)));
            else setKbIdx((r + 1) * KB_COLS + c);
          }
        } else {
          const s = kbIdx - 36;
          if (k === 'left') { if (s > 0) popNav(); setKbIdx(36 + Math.max(0, s - 1)); }
          else if (k === 'right') {
            if (s === 2 && results.length) { popNav(); setZone('results'); }
            else if (s === 2 && showHistory) { popNav(); setZone('history'); setHistIdx(0); }
            else { if (s < 2) popNav(); setKbIdx(36 + Math.min(2, s + 1)); }
          } else if (k === 'up') { popNav(); setKbIdx(5 * KB_COLS + s * 2); }
        }
      } else if (zone === 'history') {
        if (k === 'ok') { const q = hist[histIdx]; if (q) { setQuery(q); setZone('kb'); } }
        else if (k === 'left') { popNav(); setZone('kb'); }
        else if (k === 'up') { if (histIdx > 0) popNav(); setHistIdx(i => Math.max(0, i - 1)); }
        else if (k === 'down') { if (histIdx < hist.length - 1) popNav(); setHistIdx(i => Math.min(hist.length - 1, i + 1)); }
      } else {
        const r = Math.floor(resIdx / RES_COLS), c = resIdx % RES_COLS;
        if (k === 'ok') { if (results[resIdx]) { saveHist(query.trim()); onOpenDetail(results[resIdx]); } }
        else if (k === 'left') { popNav(); if (c === 0) setZone('kb'); else setResIdx(resIdx - 1); }
        else if (k === 'right') { if (resIdx < results.length - 1) popNav(); setResIdx(i => Math.min(results.length - 1, i + 1)); }
        else if (k === 'up') { if (r > 0) { popNav(); setResIdx(resIdx - RES_COLS); } }
        else if (k === 'down') { if (resIdx + RES_COLS < results.length) { popNav(); setResIdx(resIdx + RES_COLS); } }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, zone, kbIdx, resIdx, results, pressKey, onClose, onOpenDetail, hist, histIdx, query, saveHist]);

  const focusedResRow = Math.floor(resIdx / RES_COLS);

  return (
    <div className="tv-fade-in" style={{ height: '100%', display: 'flex', padding: '3vw 4vw' }} data-testid="tv-search">
      {/* Keyboard side */}
      <div style={{ width: '28vw', flex: 'none' }}>
        <div className={`tv-btn ${zone === 'back' ? 'tv-focused' : ''}`} onClick={onClose}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5vw', padding: '0.45vw 1.2vw', borderRadius: '2vw',
            background: zone === 'back' ? '#E9641A' : 'rgba(255,255,255,0.12)', cursor: 'pointer', fontSize: '1vw', fontWeight: 700, marginBottom: '1.2vw' }}
          data-testid="tv-back-btn">
          ← Retour
        </div>
        <h2 style={{ fontSize: '1.8vw', fontWeight: 800, marginBottom: '1.5vw' }}>
          <Search style={{ width: '1.6vw', height: '1.6vw', display: 'inline', verticalAlign: '-0.2vw', marginRight: '0.6vw', color: '#E9641A' }} />
          Recherche
        </h2>
        <div style={{ minHeight: '3vw', borderBottom: '0.15vw solid rgba(255,255,255,0.25)', marginBottom: '1.5vw', display: 'flex', alignItems: 'center' }} data-testid="tv-search-query">
          {query ? (
            <span style={{ fontSize: '1.7vw', fontWeight: 700, letterSpacing: '0.05em' }}>{query}<span style={{ color: '#E9641A' }}>|</span></span>
          ) : (
            <span style={{ fontSize: '1.3vw', color: 'rgba(255,255,255,0.3)' }}>Rechercher un titre…</span>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${KB_COLS}, 1fr)`, gap: '0.5vw', maxWidth: '24vw' }} data-testid="tv-search-keyboard">
          {LETTERS.map((l, i) => (
            <div key={l} className={`tv-btn ${zone === 'kb' && kbIdx === i ? 'tv-focused' : ''}`}
              style={{ height: '3.6vw', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3vw', fontWeight: 700,
                background: zone === 'kb' && kbIdx === i ? '#E9641A' : 'rgba(255,255,255,0.07)' }}
              data-testid={`tv-key-${l}`}>
              {l}
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5vw', maxWidth: '24vw', marginTop: '0.5vw' }}>
          {SPECIALS.map((s, i) => (
            <div key={s} className={`tv-btn ${zone === 'kb' && kbIdx === 36 + i ? 'tv-focused' : ''}`}
              style={{ padding: '0.7vw 0', textAlign: 'center', fontSize: '0.85vw', fontWeight: 700,
                background: zone === 'kb' && kbIdx === 36 + i ? '#E9641A' : 'rgba(255,255,255,0.07)' }}
              data-testid={`tv-key-${s}`}>
              {s}
            </div>
          ))}
        </div>
        <p style={{ marginTop: '1.5vw', fontSize: '0.9vw', color: 'rgba(255,255,255,0.35)' }}>Touche Retour pour quitter la recherche</p>
      </div>

      {/* Results side */}
      <div style={{ flex: 1, paddingLeft: '3vw', overflow: 'hidden' }}>
        {results.length > 0 ? (
          <>
            <p style={{ fontSize: '1.1vw', color: 'rgba(255,255,255,0.5)', marginBottom: '1vw' }} data-testid="tv-search-results-count">
              {results.length} résultat{results.length > 1 ? 's' : ''}
            </p>
            <div style={{ transition: 'transform 0.25s ease', transform: `translateY(${-Math.max(0, focusedResRow - 1) * 12.5}vw)` }}>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${RES_COLS}, 1fr)`, gap: '1.2vw' }} data-testid="tv-search-results">
                {results.map((item, i) => (
                  <div key={item.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.4vw' }}>
                    <div className={`tv-card ${zone === 'results' && resIdx === i ? 'tv-focused' : ''}`}
                      style={{ height: '10vw' }} data-testid={`tv-search-result-${item.id}`}>
                      <img src={item.thumbnail_url || item.banner_url} alt={item.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                    </div>
                    <span style={{ fontSize: '0.95vw', color: zone === 'results' && resIdx === i ? '#fff' : 'rgba(255,255,255,0.6)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : !query.trim() && hist.length > 0 ? (
          <div data-testid="tv-search-history">
            <p style={{ fontSize: '1.1vw', color: 'rgba(255,255,255,0.5)', marginBottom: '1vw', display: 'flex', alignItems: 'center', gap: '0.5vw' }}>
              <History style={{ width: '1.1vw', height: '1.1vw' }} />
              Recherches récentes
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6vw', maxWidth: '26vw' }}>
              {hist.map((h, i) => (
                <div key={h} className={`tv-btn ${zone === 'history' && histIdx === i ? 'tv-focused' : ''}`}
                  style={{ padding: '0.8vw 1.4vw', fontSize: '1.15vw', fontWeight: 600,
                    background: zone === 'history' && histIdx === i ? '#E9641A' : 'rgba(255,255,255,0.06)' }}
                  data-testid={`tv-history-item-${i}`}>
                  {h}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ fontSize: '1.2vw', color: 'rgba(255,255,255,0.3)' }} data-testid="tv-search-empty">
              {searched && query.trim() ? `Aucun résultat pour « ${query.trim()} »` : 'Tapez un titre avec le clavier'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
